import { useTranslation } from 'react-i18next'
import { FlaskConical, X } from '@digital-twins/design-system/icons'
import { LibraryCollectionItem } from '@digital-twins/shared-types'

import { CollectionCardLayout, CollectionItemCard, CoverChip, MetaValue } from '@/components/library/CollectionItemCard'
import { ROUTES } from '@/routes/routes'
import { formatSocialDate } from '@/pages/KnowledgeHubPage/social/socialFormat'

export interface StudyCollectionCardProps {
  /** A `study` item. */
  item: LibraryCollectionItem
  /** Test ids are `collection-study-card-{index}`. */
  index: number
  /** The card's place in its section's grid. */
  layout?: CollectionCardLayout
  /** Removes the study from this collection. Omitted when the viewer cannot edit. */
  onRemove?: () => void
  /** True while the remove request is in flight. */
  isRemoving?: boolean
}

/**
 * A study: its visual cover (the study cover image, or a generated cover
 * without one), the title, the report headline and the date. Twin portraits
 * stay off the card. One link to its results. `onRemove` sits outside that link.
 */
export function StudyCollectionCard(props: StudyCollectionCardProps) {
  const { item, index, layout = 'standard', onRemove, isRemoving = false } = props

  const { t, i18n } = useTranslation('app')

  const testId = `collection-study-card-${index}`
  const date = formatSocialDate(item.occurredAt, i18n.language)

  const coverChips = (
    <CoverChip testId={`${testId}-kind`}>
      <FlaskConical className="h-3 w-3" aria-hidden="true" />
      {t('library.collections.resources.kindSingular.study')}
    </CoverChip>
  )

  const cornerAction = onRemove ? (
    <button
      type="button"
      className="flex h-7 w-7 items-center justify-center rounded-full border border-card-border bg-background/90 text-foreground shadow-sm transition-colors hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
      aria-label={t('library.collections.resources.removeItem')}
      disabled={isRemoving}
      data-testid={`${testId}-remove`}
      onClick={onRemove}
    >
      <X className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  ) : null

  return (
    <CollectionItemCard
      item={item}
      href={ROUTES.studyResults(item.token)}
      testId={testId}
      coverChips={coverChips}
      layout={layout}
      portraits={[]}
      compact
      cornerAction={cornerAction}
      meta={date ? <MetaValue testId={`${testId}-date`}>{date}</MetaValue> : null}
      cta={t('library.collections.resources.cta.study')}
    />
  )
}
